import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { supabase } from "@/lib/supabase";
import {
  matchSalesDeskQuoteToLead,
  salesDeskLeadContactFromRow,
  type SalesDeskLeadContact,
  type SalesDeskQuoteMatch,
} from "./salesDeskQuoteMatch";

export type SalesDeskReturnedQuote = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  match: SalesDeskQuoteMatch;
  leadName: string | null;
};

const INBOX_REASON: Record<Exclude<SalesDeskQuoteMatch, { kind: "attached" }>["reason"], string> = {
  staff: "This contact belongs to someone on staff. It stays here until a person matches it.",
  missing: "It needs both an email and a phone before it can attach.",
  none: "No open lead has this email and phone.",
  many: "More than one open lead has this email and phone.",
};

export function salesDeskQuoteInboxCopy(match: SalesDeskQuoteMatch): string | null {
  return match.kind === "inbox" ? INBOX_REASON[match.reason] : null;
}

export function useSalesDeskConfigure(): {
  quotes: SalesDeskReturnedQuote[];
  loading: boolean;
  error: string | null;
} {
  const orgId = useScopedOrganizationId();
  const auth = useAuth();
  const [quotes, setQuotes] = useState<SalesDeskReturnedQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !supabase) {
      setLoading(false);
      return;
    }
    const db = supabase;
    let cancelled = false;
    (async () => {
      const [leadRes, quoteRes] = await Promise.all([
        db
          .from("leads")
          .select("id, data")
          .eq("organization_id", orgId)
          .in("status", ["draft", "sent"])
          .limit(200),
        db
          .from("ops_inbound_customer_profiles")
          .select("id, display_name, email, phone, latest_activity_at")
          .eq("organization_id", orgId)
          .order("latest_activity_at", { ascending: false })
          .limit(20),
      ]);
      if (cancelled) return;
      if (quoteRes.error) {
        setError(quoteRes.error.message);
        setQuotes([]);
        setLoading(false);
        return;
      }
      const leads = (leadRes.data ?? [])
        .map((row) => salesDeskLeadContactFromRow({ id: String(row.id), data: row.data }))
        .filter((lead): lead is SalesDeskLeadContact => Boolean(lead));
      const leadName = new Map(leads.map((lead) => [lead.id, lead.name]));
      const staff = {
        emails: [auth.profile?.email ?? ""],
        phones: [auth.profile?.personal_phone ?? "", auth.profile?.work_phone ?? ""],
      };
      setQuotes(
        (quoteRes.data ?? []).map((row) => {
          const match = matchSalesDeskQuoteToLead(
            { email: typeof row.email === "string" ? row.email : null, phone: typeof row.phone === "string" ? row.phone : null },
            leads,
            staff,
          );
          const name =
            (typeof row.display_name === "string" && row.display_name.trim()) ||
            (typeof row.email === "string" && row.email.trim()) ||
            (typeof row.phone === "string" && row.phone.trim()) ||
            "Returned quote";
          return {
            id: String(row.id),
            name,
            email: typeof row.email === "string" ? row.email : null,
            phone: typeof row.phone === "string" ? row.phone : null,
            match,
            leadName: match.kind === "attached" ? leadName.get(match.leadId) ?? null : null,
          };
        }),
      );
      setError(null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.profile?.email, auth.profile?.personal_phone, auth.profile?.work_phone, orgId]);

  return { quotes, loading, error };
}
